import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Detekter om skærmen er under 1200px
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1200;
      setIsMobile(mobile);
      if (!mobile) {
        setIsOpen(false); // Luk burgermenu på desktop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Luk dropdown når man klikker udenfor
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Luk menu og dropdown ved ruteskift
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const productCategories = [
    { name: 'Chokolade', path: '/produkter/chokolade' },
    { name: 'Thy Kukies', path: '/produkter/thy-kukies' },
    { name: 'Kager', path: '/produkter/kager' },
    { name: 'Gaveæsker & Gavekurve', path: '/produkter/gaveaesker-gavekurve' },
    { name: 'Sæsonvarer', path: '/produkter/saesonvarer' }
  ];

  return (
    <header className="sticky top-0 z-50 bg-cream-bg/95 backdrop-blur-md border-b border-choc-dark/10 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=360,quality=90,format=auto/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/chokolenten-logo.png" 
              alt="Chokolenten Logo" 
              className="h-16 sm:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          {!isMobile && (
            <nav className="flex items-center gap-8">
              <Link
                to="/"
                className={`font-sans text-sm font-medium tracking-wide transition-colors hover:text-berry-accent ${
                  isActive('/') ? 'text-berry-accent font-semibold' : 'text-choc-dark/80'
                }`}
              >
                Forside
              </Link>

              {/* Dropdown til Produkter */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center gap-1.5 font-sans text-sm font-medium tracking-wide transition-colors hover:text-berry-accent cursor-pointer ${
                    isActive('/produkter') ? 'text-berry-accent font-semibold' : 'text-choc-dark/80'
                  }`}
                >
                  Produkter
                  <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-xl bg-cream-light border border-choc-dark/10 shadow-lg py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    {productCategories.map((cat) => (
                      <Link
                        key={cat.path}
                        to={cat.path}
                        className={`block px-4 py-2.5 text-sm transition-colors hover:bg-choc-dark/5 ${
                          location.pathname === cat.path ? 'text-berry-accent font-semibold bg-berry-accent/5' : 'text-choc-dark/80'
                        }`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/om-os"
                className={`font-sans text-sm font-medium tracking-wide transition-colors hover:text-berry-accent ${
                  isActive('/om-os') ? 'text-berry-accent font-semibold' : 'text-choc-dark/80'
                }`}
              >
                Om os
              </Link>

              <Link
                to="/galleri"
                className={`font-sans text-sm font-medium tracking-wide transition-colors hover:text-berry-accent ${
                  isActive('/galleri') ? 'text-berry-accent font-semibold' : 'text-choc-dark/80'
                }`}
              >
                Galleri
              </Link>

              <Link
                to="/kontakt"
                className={`font-sans text-sm font-medium tracking-wide transition-colors hover:text-berry-accent ${
                  isActive('/kontakt') ? 'text-berry-accent font-semibold' : 'text-choc-dark/80'
                }`}
              >
                Kontakt
              </Link>
            </nav>
          )}

          {/* Burgermenu Knap (< 1200px) */}
          {isMobile && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-choc-dark hover:bg-choc-dark/5 transition-colors cursor-pointer"
              aria-label="Åben menu"
            >
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
            </button>
          )}
        </div>
      </div>

      {/* Mobil Navigation (< 1200px) */}
      {isMobile && isOpen && (
        <div className="border-t border-choc-dark/10 bg-cream-light py-4 px-4 shadow-inner animate-in slide-in-from-top duration-200">
          <nav className="flex flex-col gap-2">
            <Link
              to="/"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                isActive('/') ? 'bg-berry-accent/10 text-berry-accent font-semibold' : 'text-choc-dark/80 hover:bg-choc-dark/5'
              }`}
            >
              Forside
            </Link>

            {/* Produkter Sektion i Mobil Menu */}
            <div className="px-4 py-2">
              <div className="font-sans text-xs font-bold uppercase tracking-wider text-choc-dark/40 mb-2">
                Produkter
              </div>
              <div className="flex flex-col gap-1 pl-2 border-l border-choc-dark/10">
                {productCategories.map((cat) => (
                  <Link
                    key={cat.path}
                    to={cat.path}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                      location.pathname === cat.path ? 'text-berry-accent font-semibold bg-berry-accent/5' : 'text-choc-dark/80 hover:bg-choc-dark/5'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/om-os"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                isActive('/om-os') ? 'bg-berry-accent/10 text-berry-accent font-semibold' : 'text-choc-dark/80 hover:bg-choc-dark/5'
              }`}
            >
              Om os
            </Link>

            <Link
              to="/galleri"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                isActive('/galleri') ? 'bg-berry-accent/10 text-berry-accent font-semibold' : 'text-choc-dark/80 hover:bg-choc-dark/5'
              }`}
            >
              Galleri
            </Link>

            <Link
              to="/kontakt"
              className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                isActive('/kontakt') ? 'bg-berry-accent/10 text-berry-accent font-semibold' : 'text-choc-dark/80 hover:bg-choc-dark/5'
              }`}
            >
              Kontakt
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
